// @flow

import React, { Component } from "react";

import {
  chain,
  identity,
  intersection,
  pipe,
  prop,
  range,
  uniq,
  uniqBy,
} from "ramda";

import { Maybe } from "ramda-fantasy";

import { Constants } from "ag-grid-community";

import { Translate } from "@quantum/i18n";

import { type AggFuncs } from "../../../model";

import { maybeEmpty, maybeParseFloat } from "../../../util";

import "./aggregation-status-bar.css";

type Props = {
  api: any,
  aggFuncs: Array<$Keys<AggFuncs>>,
};

type State = {
  aggResults: {
    [$Keys<AggFuncs>]: number,
  },
};

export default class AggregationStatusBar extends Component<Props, State> {
  state = {
    aggResults: {},
  };

  onRangeSelectionChange = () => {
    const { aggFuncs } = this.props;
    const {
      aggFuncService,
      pinnedRowModel,
      rangeController,
      rowModel,
    } = this.props.api;
    const api = this.props.api;

    const getRowNode = gridRow => {
      switch (gridRow.floating) {
        case Constants.PINNED_TOP:
          return pinnedRowModel.getPinnedTopRowData()[gridRow.rowIndex];
        case Constants.PINNED_BOTTOM:
          return pinnedRowModel.getPinnedBottomRowData()[gridRow.rowIndex];
        default:
          return rowModel.getRow(gridRow.rowIndex);
      }
    };

    const cellRanges = rangeController.getCellRanges() || [];

    const columnFormatter = column =>
      Maybe(column.colDef.valueFormatter).getOrElse(null);
    const columnType = column =>
      Maybe(columnFormatter(column))
        .map(prop("type"))
        .getOrElse("string");

    const columns = cellRanges.flatMap(range => range.columns);
    const formatters = columns.map(columnFormatter);
    const types = columns.map(columnType);
    const type = types[0];
    const singleType = uniq(types).length === 1;

    const columnAggFuncs = singleType
      ? columns
          .filter(column => columnType(column) === type)
          .map(column => column.colDef.allowedAggFuncs)[0]
      : [];

    const allowedAggFunctions = intersection(aggFuncs, columnAggFuncs || []);

    const formatter = singleType ? formatters[0] || identity : identity;

    const extractUniqueCells = pipe(
      chain(({ columns, startRow, endRow }) => {
        const startRowIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
        const endRowIndex = Math.max(startRow.rowIndex, endRow.rowIndex);

        const rows = range(startRowIndex, endRowIndex + 1).map(row =>
          rowModel.getRow(row)
        );

        return columns.flatMap(col =>
          rows.map(row => ({
            id: [row.id, col.colId].join("-"),
            value: Maybe(getRowNode(row)).chain(node =>
              maybeEmpty(api.getValue(col, node))
            ),
          }))
        );
      }),
      uniqBy(prop("id"))
    );

    const cells = extractUniqueCells(cellRanges);

    const values = cells
      .map(({ value }) => {
        switch (type) {
          case "number":
          case "percentage":
          case "money":
            return value.chain(maybeParseFloat);

          case "date":
          case "string":
          default:
            return value;
        }
      })
      .map(value => value.getOrElse(null));

    if (values.length > 0) {
      this.setState({
        aggResults: {
          ...allowedAggFunctions.reduce(
            (result, func) => ({
              ...result,
              [func]: formatter({
                value: aggFuncService.getAggFunc(func).call(null, values),
              }),
            }),
            {}
          ),
          count: aggFuncService.getAggFunc("count").call(null, values),
        },
      });
    } else {
      this.setState({ aggResults: {} });
    }
  };

  componentWillMount() {
    this.props.api.addEventListener(
      "rangeSelectionChanged",
      this.onRangeSelectionChange
    );
  }

  componentWillUnmount() {
    this.props.api.removeEventListener(
      "rangeSelectionChanged",
      this.onRangeSelectionChange
    );
  }

  render() {
    return (
      <ul className="AggregationStatusBar">
        {this.props.aggFuncs
          .filter(agg => this.state.aggResults[agg] != null)
          .map(agg => (
            <li key={agg} className="AggregationStatusBar__item">
              <span className="AggregationStatusBar__item-name">
                <Translate>{`ag-grid.${agg}`}</Translate>:
              </span>
              <span>{this.state.aggResults[agg]}</span>
            </li>
          ))}
      </ul>
    );
  }
}
